import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase';
import { 
  Search, Play, Users, Award, Star, Clock, ArrowRight, BookOpen, 
  ChevronDown, Globe, ChevronLeft, ChevronRight, Facebook, Twitter, 
  Instagram, Linkedin, Youtube, Briefcase, Target, UserCheck, Zap, 
  ArrowUpRight, Bell, ShoppingCart, Heart, BarChart3, 
  MessageSquare, CreditCard, Calendar, Wallet, History, User, 
  Edit, HelpCircle, LogOut, GraduationCap, TrendingUp, 
  Globe as GlobeIcon, Shield, CheckCircle, Menu, X, Home as HomeIcon,
  FolderOpen, Archive, Upload, Eye, Mail, Settings, Download
} from 'lucide-react';
import './VideoCourseHome.css';

const VideoCourseHomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [navSearchQuery, setNavSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [currentCourseIndex, setCurrentCourseIndex] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const userDropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(app), (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await getAuth(app).signOut();
      navigate('/MHMV-profile/jobseeker-login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/MHMV-profile/search-results', { state: { search: searchQuery } });
    }
  };
  
  const handleNavSearch = (e) => {
    e.preventDefault();
    if (navSearchQuery.trim()) {
      navigate('/MHMV-profile/search-results', { state: { search: navSearchQuery } });
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setShowMobileMenu(false);
    setShowUserDropdown(false);
  };

  const handleProfileClick = () => {
    navigate('/MHMV-profile/profile');
    setShowUserDropdown(false);
  };

  const nextCourse = () => {
    setCurrentCourseIndex((prev) => (prev + 3) % featuredCourses.length);
  };

  const prevCourse = () => {
    setCurrentCourseIndex((prev) => (prev - 3 + featuredCourses.length) % featuredCourses.length);
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'te', name: 'Telugu' },
    { code: 'hi', name: 'Hindi' }
  ];

  const featuredCourses = [
    {
      id: 1,
      title: 'Complete Web Development Bootcamp',
      instructor: 'John Smith',
      rating: 4.8,
      students: 12543,
      duration: '32h 15m',
      level: 'Beginner',
      price: 0,
      category: 'Information Technology (IT)',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    },
    {
      id: 2,
      title: 'Data Science Fundamentals',
      instructor: 'Sarah Johnson',
      rating: 4.7,
      students: 8921,
      duration: '28h 45m',
      level: 'Intermediate',
      price: 0,
      category: 'Data Science',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    },
    {
      id: 3,
      title: 'Mobile App Development with React Native',
      instructor: 'Mike Chen',
      rating: 4.9,
      students: 11245,
      duration: '35h 20m',
      level: 'Advanced',
      price: 0,
      category: 'Mobile Development',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    },
    {
      id: 4,
      title: 'Business Management Essentials',
      instructor: 'Emma Wilson',
      rating: 4.6,
      students: 7563,
      duration: '25h 30m',
      level: 'Beginner',
      price: 0,
      category: 'Business',
      image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    },
    {
      id: 5,
      title: 'Digital Marketing Mastery',
      instructor: 'Alex Rodriguez',
      rating: 4.5,
      students: 9342,
      duration: '30h 15m',
      level: 'Intermediate',
      price: 0,
      category: 'Marketing',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    },
    {
      id: 6,
      title: 'Personal Development Psychology',
      instructor: 'Dr. Sarah Miller',
      rating: 4.9,
      students: 6213,
      duration: '18h 45m',
      level: 'Beginner',
      price: 0,
      category: 'Personal Development',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    }
  ];

  const categories = [
    { name: 'Information Technology (IT)', courses: 100 },
    { name: 'Language', courses: 102 },
    { name: 'Business', courses: 103 },
    { name: 'Management', courses: 104 },
    { name: 'English', courses: 105 },
    { name: 'Personal Development', courses: 106 },
    { name: 'Sales & Marketing', courses: 107 },
    { name: 'Engineering & Construction', courses: 824 },
  ];

  const careerOptions = [
    {
      icon: <Briefcase className="vchome-career-icon" />,
      title: 'I want to apply for a job',
      description: 'Get job-ready with certified courses'
    },
    {
      icon: <Target className="vchome-career-icon" />,
      title: 'I want to find a new career',
      description: 'Discover new career paths'
    },
    {
      icon: <UserCheck className="vchome-career-icon" />,
      title: 'I want to upskill in my current career',
      description: 'Advance your current skills'
    }
  ];

  const businessSolutions = [
    {
      icon: <Zap className="vchome-business-icon" />,
      title: 'A Faster Way For Your Employees To Grow And Upskill',
      description: 'Our range of solutions has something to offer all businesses and organizations. Whether you\'re a non-profit or a commercial entity, we can tailor a plan for your needs.',
      cta: 'Explore MHMV For Business'
    }
  ];

  const displayedCourses = featuredCourses.slice(currentCourseIndex, currentCourseIndex + 3);
  
  const userData = {
    name: user?.displayName || (user?.email ? user.email.split('@')[0] : 'User'),
    email: user?.email || 'user@example.com',
    profileCompletion: user ? 75 : 0
  };

  // Navigation menu items
  const navMenuItems = [
    { label: 'Home', path: '/MHMV-profile/video-course-home' },
    { label: 'Manage Courses',  path: '/MHMV-profile/manage-courses' },
    
  ];

  return (
    <div className="vchome-container">
      {/* Professional Header */}
      <header className="vchome-header">
        <div className="vchome-header-container">
          {/* Logo Section */}
          <div className="vchome-logo-section">
            <button 
              className="vchome-mobile-menu-btn"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="vchome-brand" onClick={() => handleNavigation('/MHMV-profile/video-course-home')}>
              
              <span className="vchome-brand-name">MHMV</span>
            </div>
          </div>

          

          {/* Search Bar */}
          <div className="vchome-search-container">
            <form onSubmit={handleNavSearch} className="vchome-search-form">
              <div className="vchome-search-box">
                <Search className="vchome-search-icon" />
                <input
                  type="text"
                  placeholder="Search for courses..."
                  value={navSearchQuery}
                  onChange={(e) => setNavSearchQuery(e.target.value)}
                  className="vchome-search-input"
                />
                <button type="submit" className="vchome-search-btn">
                  Search
                </button>
              </div>
            </form>
          </div>

          {/* Navigation Menu */}
          <nav className="vchome-nav-menu">
            {navMenuItems.map((item) => (
              <button
                key={item.label}
                className={`vchome-nav-item ${window.location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Header Actions */}
          <div className="vchome-header-actions">
            {/* Language Selector */}
            <div className="vchome-language-selector" ref={languageDropdownRef}>
              <button 
                className="vchome-language-btn"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <Globe size={18} />
                <span>{selectedLanguage}</span>
                <ChevronDown size={16} />
              </button>
              {showLanguageDropdown && (
                <div className="vchome-language-dropdown-menu">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      className="vchome-language-option"
                      onClick={() => {
                        setSelectedLanguage(language.name);
                        setShowLanguageDropdown(false);
                      }}
                    >
                      {language.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Icons */}
            <div className="vchome-shopping-icons">
              <button className="vchome-icon-btn" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                <ShoppingCart size={20} />
                <span className="vchome-icon-badge">3</span>
              </button>
              <button className="vchome-icon-btn" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                <Heart size={20} />
              </button>
            </div>

            {/* User Section */}
            {user ? (
              <div className="vchome-user-container" ref={userDropdownRef}>
                <button 
                  className="vchome-user-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <div className="vchome-user-avatar-small">
                    {userData.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="vchome-user-name">{userData.name}</span>
                  <ChevronDown size={16} />
                </button>
                
                {showUserDropdown && (
                  <div className="vchome-user-dropdown">
                    {/* Clickable Header Section */}
                    <div 
                      className="vchome-user-dropdown-header clickable"
                      onClick={handleProfileClick}
                    >
                      <div className="vchome-user-dropdown-avatar">
                        {userData.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="vchome-user-dropdown-info">
                        <div className="vchome-user-dropdown-name">
                          {userData.name}
                        </div>
                        <div className="vchome-user-dropdown-email">
                          {userData.email}
                        </div>
                        
                      </div>
                    </div>
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    {/* Shopping Section */}
                    <div className="vchome-user-dropdown-section">
                      <h4 className="vchome-user-dropdown-section-title">Shopping</h4>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <ShoppingCart size={18} />
                        <div className="vchome-dropdown-item-content">
                          <span className="vchome-dropdown-item-title">My cart</span>
                          
                        </div>
                      </button>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <Heart size={18} />
                        <span className="vchome-dropdown-item-title">Wishlist</span>
                      </button>
                    </div>
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    {/* Learning Section */}
                    <div className="vchome-user-dropdown-section">
                      <h4 className="vchome-user-dropdown-section-title">Learning</h4>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <GraduationCap size={18} />
                        <span className="vchome-dropdown-item-title">My learning</span>
                      </button>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/manage-courses')}>
                        <BarChart3 size={18} />
                        <span className="vchome-dropdown-item-title">Instructor dashboard</span>
                      </button>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <TrendingUp size={18} />
                        <span className="vchome-dropdown-item-title">Learning analytics</span>
                      </button>
                    </div>
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    {/* Notifications Section */}
                    <div className="vchome-user-dropdown-section">
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <Bell size={18} />
                        <div className="vchome-dropdown-item-content">
                          <span className="vchome-dropdown-item-title">Notifications</span>
                          
                        </div>
                      </button>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <MessageSquare size={18} />
                        <div className="vchome-dropdown-item-content">
                          <span className="vchome-dropdown-item-title">Messages</span>
                          
                        </div>
                      </button>
                    </div>
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    {/* Account Section */}
                    
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    
                    
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    {/* Profile Section */}
                    <div className="vchome-user-dropdown-section">
                      <button className="vchome-user-dropdown-item" onClick={handleProfileClick}>
                        <User size={18} />
                        <span className="vchome-dropdown-item-title">Public profile</span>
                      </button>
                      <button className="vchome-user-dropdown-item" onClick={handleProfileClick}>
                        <Edit size={18} />
                        <span className="vchome-dropdown-item-title">Edit profile</span>
                      </button>
                    </div>
                    
                    <div className="vchome-user-dropdown-divider"></div>
                    
                    {/* Support & Logout Section */}
                    <div className="vchome-user-dropdown-section">
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/contact')}>
                        <HelpCircle size={18} />
                        <span className="vchome-dropdown-item-title">Help and Support</span>
                      </button>
                      <button className="vchome-user-dropdown-item" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                        <Shield size={18} />
                        <span className="vchome-dropdown-item-title">Privacy & safety</span>
                      </button>
                      <button 
                        className="vchome-user-dropdown-item vchome-user-logout"
                        onClick={handleLogout}
                      >
                        <LogOut size={18} />
                        <span className="vchome-dropdown-item-title">Log out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="vchome-auth-buttons">
                <button 
                  className="vchome-login-btn"
                  onClick={() => handleNavigation('/MHMV-profile/jobseeker-login')}
                >
                  Log in
                </button>
                <button 
                  className="vchome-signup-btn"
                  onClick={() => handleNavigation('/MHMV-profile/modal-register')}
                >
                  Sign up
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="vchome-mobile-menu" ref={mobileMenuRef}>
            {navMenuItems.map((item) => (
              <button
                key={item.label}
                className="vchome-mobile-menu-item"
                onClick={() => handleNavigation(item.path)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
            <div className="vchome-mobile-menu-divider"></div>
            <button className="vchome-mobile-menu-item" onClick={() => handleNavigation('/contact')}>
              <HelpCircle size={18} />
              <span>Help & Support</span>
            </button>
          </div>
        )}
      </header>

            {/* Hero Section with Background Image */}
      <section className="vchome-hero-bg">
        <div className="vchome-hero-bg-overlay"></div>
        <div className="vchome-hero-bg-content">
          <div className="vchome-hero-bg-top">
            <h1 className="vchome-hero-bg-title">
              Welcome back, {userData.name}
            </h1>
            <p className="vchome-hero-bg-subtitle">
              Add occupation and interests
            </p>
            <div className="vchome-hero-bg-divider"></div>
          </div>
          
          <div className="vchome-hero-bg-main">
            <h2 className="vchome-hero-bg-headline">
              Learning that gets you
            </h2>
            <p className="vchome-hero-bg-desc">
              Skills for your present (and your future). Get started with us.
            </p>
            
            <div className="vchome-hero-bg-divider-main"></div>
            
            <div className="vchome-hero-bg-bottom">
              <p className="vchome-hero-bg-action">
                <strong>Learn to start learning</strong>
              </p>
              <button 
                className="vchome-hero-bg-btn"
                onClick={() => handleNavigation('/MHMV-profile/video-courses')}
              >
                Start Exploring
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="vchome-stats-section">
        <div className="vchome-stats-container">
          <div className="vchome-stats-grid">
            <div className="vchome-stat-item">
              <div className="vchome-stat-main">
                <span className="vchome-stat-label">Rated Excellent</span>
                <div className="vchome-stat-stars">
                  <Star size={16} fill="#fbbf24" color="#fbbf24" />
                  <Star size={16} fill="#fbbf24" color="#fbbf24" />
                  <Star size={16} fill="#fbbf24" color="#fbbf24" />
                  <Star size={16} fill="#fbbf24" color="#fbbf24" />
                  <Star size={16} fill="#fbbf24" color="#fbbf24" />
                </div>
              </div>
            </div>
            <div className="vchome-stat-item">
              <div className="vchome-stat-main">
                <span className="vchome-stat-number">50+</span>
                <span className="vchome-stat-unit">Thousand</span>
              </div>
              <span className="vchome-stat-label">Learners</span>
            </div>
            <div className="vchome-stat-item">
              <div className="vchome-stat-main">
                <span className="vchome-stat-number">15+</span>
                <span className="vchome-stat-unit">Thousand</span>
              </div>
              <span className="vchome-stat-label">Graduates</span>
            </div>
            <div className="vchome-stat-item">
              <div className="vchome-stat-main">
                <span className="vchome-stat-number">10</span>
                <span className="vchome-stat-unit">Countries</span>
              </div>
              <span className="vchome-stat-label">Worldwide</span>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="vchome-courses-section">
        <div className="vchome-container-section">
          <div className="vchome-section-header">
            <h2 className="vchome-section-title">Featured Courses</h2>
            <button 
              className="vchome-view-all-btn"
              onClick={() => handleNavigation('/MHMV-profile/video-courses')}
            >
              View All Courses
            </button>
          </div>

          <div className="vchome-courses-container">
            <button className="vchome-course-nav-arrow vchome-course-nav-prev" onClick={prevCourse}>
              <ChevronLeft size={32} />
            </button>

            <div className="vchome-courses-grid">
              {displayedCourses.map((course) => (
                <div key={course.id} className="vchome-course-card" onClick={() => handleNavigation(`/MHMV-profile/video-courses`)}>
                  <div className="vchome-course-image">
                    <div className="vchome-course-badge">{course.level}</div>
                    <img 
                      src={course.image} 
                      alt={course.title}
                      className="vchome-course-img"
                    />
                  </div>
                  
                  <div className="vchome-course-content">
                    <h3 className="vchome-course-title">{course.title}</h3>
                    <p className="vchome-course-instructor">By {course.instructor}</p>
                    <p className="vchome-course-category">{course.category}</p>
                    
                    <div className="vchome-course-meta">
                      <div className="vchome-meta-item">
                        <Star className="vchome-meta-icon" />
                        <span>{course.rating}</span>
                      </div>
                      <div className="vchome-meta-item">
                        <Users className="vchome-meta-icon" />
                        <span>{course.students.toLocaleString()}</span>
                      </div>
                      <div className="vchome-meta-item">
                        <Clock className="vchome-meta-icon" />
                        <span>{course.duration}</span>
                      </div>
                    </div>
                    
                    <div className="vchome-course-footer">
                      <div className="vchome-course-price">
                        FREE
                      </div>
                      <button className="vchome-enroll-btn" onClick={(e) => {
                        e.stopPropagation();
                        handleNavigation('/MHMV-profile/video-courses');
                      }}>
                        Enroll Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="vchome-course-nav-arrow vchome-course-nav-next" onClick={nextCourse}>
              <ChevronRight size={32} />
            </button>
          </div>
        </div>
      </section>

      {/* Career Ready Plan Section */}
      <section className="vchome-career-ready-section">
        <div className="vchome-container-section">
          <div className="vchome-career-ready-content">
            <h2 className="vchome-career-ready-title">
              Not sure where to begin? Or even what you want to do?
            </h2>
            <p className="vchome-career-ready-subtitle">
              Answer a few short questions and we'll provide you with a Career Ready Plan!
            </p>
            <div className="vchome-career-options-grid">
              {careerOptions.map((option, index) => (
                <div key={index} className="vchome-career-option-card" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                  <div className="vchome-career-option-icon">
                    {option.icon}
                  </div>
                  <h3 className="vchome-career-option-title">{option.title}</h3>
                  <p className="vchome-career-option-description">{option.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="vchome-categories-section">
        <div className="vchome-container-section">
          <h2 className="vchome-section-title">Explore 10+ Free Online Courses</h2>
          <div className="vchome-categories-grid">
            {categories.map((category, index) => (
              <div key={index} className="vchome-category-card" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                <h3 className="vchome-category-name">{category.name}</h3>
                <p className="vchome-category-courses">{category.courses} Courses →</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Solutions Section */}
      <section className="vchome-business-solutions-section">
        <div className="vchome-container-section">
          {businessSolutions.map((solution, index) => (
            <div key={index} className="vchome-business-solution-card">
              <div className="vchome-business-solution-content">
                <div className="vchome-business-solution-icon">
                  {solution.icon}
                </div>
                <h2 className="vchome-business-solution-title">{solution.title}</h2>
                <p className="vchome-business-solution-description">{solution.description}</p>
                <button className="vchome-business-solution-cta" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>
                  {solution.cta}
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="vchome-footer">
        <div className="vchome-footer-content">
          <div className="vchome-footer-main">
            <div className="vchome-footer-brand">
              <BookOpen className="vchome-footer-icon" />
              <span className="vchome-footer-brand-text">MHMV Learning Platform</span>
            </div>
            <p className="vchome-footer-description">
              A Faster Way For Your Employees To Grow And Upskill
            </p>
            <div className="vchome-footer-social">
              <a href="#" className="vchome-social-link">
                <Facebook size={18} />
              </a>
              <a href="#" className="vchome-social-link">
                <Twitter size={18} />
              </a>
              <a href="#" className="vchome-social-link">
                <Instagram size={18} />
              </a>
              <a href="#" className="vchome-social-link">
                <Linkedin size={18} />
              </a>
              <a href="#" className="vchome-social-link">
                <Youtube size={18} />
              </a>
            </div>
          </div>
          
          <div className="vchome-footer-links-grid">
            <div className="vchome-footer-column">
              <h4>Courses</h4>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Business</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Engineering & Construction</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>English</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Health</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Information Technology (IT)</a>
            </div>
            
            <div className="vchome-footer-column">
              <h4>Company</h4>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Our Story</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Board of Directors</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Our Team & Culture</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Our Unique HQ</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Open Positions</a>
            </div>
            
            <div className="vchome-footer-column">
              <h4>Resources</h4>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Accreditation</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>All Certificates</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>All Diplomas</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>All Free Courses</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>MHMV Testimonials</a>
            </div>
            
            <div className="vchome-footer-column">
              <h4>Career</h4>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/job-search')}>Build Your Resume</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/job-search')}>Explore Job Vacancies</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>MHMV Career Guide</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Career Ready Plan</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Aptitude Test</a>
            </div>
            
            <div className="vchome-footer-column">
              <h4>Platform</h4>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Access Free LMS</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Affiliate Programme</a>
              <a href="#" onClick={handleProfileClick}>MHMV Profile</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/upload-courses')}>Create Courses</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Download App & Learn Offline</a>
            </div>
          </div>
        </div>
        
        <div className="vchome-footer-bottom">
          <div className="vchome-footer-bottom-content">
            <p>&copy; 2025 MHMV Learning Platform. All rights reserved.</p>
            <div className="vchome-footer-legal">
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Privacy Policy</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Terms of Service</a>
              <a href="#" onClick={() => handleNavigation('/MHMV-profile/video-courses')}>Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VideoCourseHomePage;